import { Box, styled, Tooltip, useTheme } from '@mui/material';
import { BodyText } from '@/components/BodyText';
import { ActivityMetrics, ActivityPeriodDays } from '@/hooks/api/tenants/useGetTenantActivityMetrics';
import { FEATURE_DESCRIPTIONS, newThisPeriodDescription, trendDescription } from './activityDescriptions';
import { describeTrend, NEW_THIS_PERIOD, Trend } from './activityTrend';

type FeatureUsage = ActivityMetrics['featureUsage'];
type Feature = keyof FeatureUsage;

const FEATURE_LABELS: Record<Feature, string> = {
  search_text: 'Text search',
  search_visual: 'Visual search',
  search_geometric: 'Geometric search',
  search_part: 'Part search',
  search_composite: 'Composite search',
  search_metadata: 'Metadata search',
  compare: 'Compare',
  download: 'Downloads',
  folder_browse: 'Folder browsing',
  folder_manage: 'Folder management',
  metadata_edit: 'Metadata edits',
  viewer_open: '3D viewer',
  taxonomy_manage: 'Taxonomy management',
  taxonomy_label: 'Taxonomy labels',
  upload: 'Uploads',
  report: 'Reports',
};

const FEATURES_EVERY_TENANT_USES: ReadonlySet<Feature> = new Set<Feature>(['folder_browse']);

const Rows = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'minmax(140px, max-content) 4em minmax(80px, 1fr) max-content',
  columnGap: '12px',
  rowGap: '6px',
  alignItems: 'center',
});

const BarTrack = styled(Box)(({ theme }) => ({
  height: '8px',
  borderRadius: '4px',
  backgroundColor: theme.palette.action.hover,
  overflow: 'hidden',
}));

const BarFill = styled(Box)({
  height: '100%',
  borderRadius: '4px',
});

export function rankFeatures(current: FeatureUsage, previous: FeatureUsage): Feature[] {
  return (Object.keys(FEATURE_LABELS) as Feature[])
    .filter(feature => !FEATURES_EVERY_TENANT_USES.has(feature))
    .filter(feature => current[feature] > 0 || previous[feature] > 0)
    .sort((a, b) => current[b] - current[a]);
}

const trendTooltip = (trend: Trend, days: ActivityPeriodDays) =>
  trend.label === NEW_THIS_PERIOD ? newThisPeriodDescription(days) : trendDescription(days);

export const FeatureUsageList = ({
  current,
  previous,
  days,
}: {
  current: FeatureUsage;
  previous: FeatureUsage;
  days: ActivityPeriodDays;
}) => {
  const theme = useTheme();
  const features = rankFeatures(current, previous);
  const busiest = Math.max(1, ...features.map(feature => current[feature]));

  if (features.length === 0) {
    return (
      <BodyText color='text.secondary' data-testid='no-feature-usage'>
        No feature activity in this period.
      </BodyText>
    );
  }

  return (
    <Box display='flex' flexDirection='column' gap='8px'>
      <BodyText fontWeight={600}>Features used</BodyText>
      <Rows data-testid='feature-usage-list'>
        {features.map(feature => {
          const trend = describeTrend(current[feature], previous[feature]);
          return (
            <Box key={feature} display='contents' data-testid={`feature-usage-${feature}`}>
              <BodyText>{FEATURE_LABELS[feature]}</BodyText>
              <Tooltip title={FEATURE_DESCRIPTIONS[feature]} placement='top' arrow>
                <BodyText textAlign='right' data-testid={`feature-usage-${feature}-count`}>
                  {current[feature].toLocaleString()}
                </BodyText>
              </Tooltip>
              <Tooltip title={FEATURE_DESCRIPTIONS[feature]} placement='top' arrow>
                <BarTrack data-testid={`feature-usage-${feature}-bar`}>
                  <BarFill
                    sx={{
                      width: `${(current[feature] / busiest) * 100}%`,
                      backgroundColor: theme.palette.secondary.main,
                    }}
                  />
                </BarTrack>
              </Tooltip>
              <Tooltip title={trendTooltip(trend, days)} placement='top' arrow>
                <BodyText
                  fontSize='0.75rem'
                  color={trend.direction === 'up' ? 'success.main' : 'text.secondary'}
                  data-testid={`feature-usage-${feature}-trend`}
                >
                  {trend.label}
                </BodyText>
              </Tooltip>
            </Box>
          );
        })}
      </Rows>
    </Box>
  );
};
